import React, { useState, useEffect } from 'react';
import { Shield, Lock, FileText, Scale } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MarketingLayout } from '../components/shell/MarketingLayout';
import { LegalHero } from '../components/shell/LegalHero';
import { cn } from '../lib/utils';
import { SEO } from '../components/SEO';

export const PrivacyPage = () => {
  const brandName = 'OrgSets';
  const proseClasses = "prose prose-lg max-w-none leading-relaxed prose-headings:font-black prose-headings:tracking-tight prose-p:text-black prose-li:text-black prose-strong:text-black dark:prose-headings:text-white dark:prose-p:text-white dark:prose-li:text-white dark:prose-strong:text-white";

  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['introduction', 'data-collection', 'data-usage', 'security', 'rights', 'regional-rights'];
      let current = sections[0];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            current = section;
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1];
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'introduction', label: 'Our Promise' },
    { id: 'data-collection', label: '1. What We Collect' },
    { id: 'data-usage', label: '2. How We Use It' },
    { id: 'security', label: '3. How We Protect It' },
    { id: 'rights', label: '4. Your Rights' },
    { id: 'regional-rights', label: '5. Regional Rights' },
  ];

  return (
    <MarketingLayout mainClassName="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
      <SEO title="Privacy Policy" description={`Read our privacy policy regarding data collection and usage on ${brandName}.`} />

      <div className="max-w-6xl mx-auto">
          
          {/* Hero Section */}
          <LegalHero 
            title="Privacy Policy"
            subtitle={`At ${brandName}, we believe privacy is a fundamental right, not an afterthought. We only collect the data we absolutely need.`}
            icon={<Shield size={14} />}
            label="Data Protection"
            version="2.4"
            updated="April 2026"
          />

          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 relative">
            
            {/* Sticky Navigation */}
            <aside className="md:w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-32 space-y-6">
                <h4 className="font-bold uppercase text-[10px] tracking-widest font-mono text-slate-400 dark:text-slate-500">
                  On this page
                </h4>
                <nav className="flex flex-col gap-3 relative before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
                  {navItems.map((item) => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`} 
                      className={cn(
                        "pl-4 py-1 text-sm font-medium transition-all duration-200 relative",
                        activeSection === item.id 
                          ? "text-brand-primary before:absolute before:inset-y-0 before:-left-px before:w-[2px] before:bg-brand-primary" 
                          : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                      )}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 max-w-3xl space-y-20">
              
              <section id="introduction" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <Scale size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">Our Promise</h2>
                </div>
                <div className={proseClasses}>
                  <p className="text-xl italic font-serif text-slate-500 dark:text-slate-400 border-l-4 border-brand-primary pl-4">
                    At {brandName}, we believe privacy is a fundamental right, not an afterthought.
                  </p>
                  <p className="mt-6">
                    This page explains how we handle your information when you use {brandName}. We operate on a simple rule: we only collect the data we absolutely need to make the app work for you and your organization. Nothing more, nothing less.
                  </p>
                </div>
              </section>

              <section id="data-collection" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <FileText size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">1. What We Collect</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    We only collect what we need to run your organization smoothly:
                  </p>
                  <ul className="list-none space-y-4 pl-0 p-6 rounded-xl border mt-6 bg-white border-gray-100 shadow-sm dark:bg-[#111] dark:border-white/10">
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                      <span><strong className="text-slate-900 dark:text-white">The Basics:</strong> Your name, email address, and login details so you can access your account.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                      <span><strong className="text-slate-900 dark:text-white">Your Group's Setup:</strong> Information about your organization's structure and who has what role.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                      <span><strong className="text-slate-900 dark:text-white">Payment Receipts:</strong> If you pay dues, our partner Stripe securely handles your credit card. We never see your card numbers; we just keep the receipt records so everyone knows who paid what.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                      <span><strong className="text-slate-900 dark:text-white">Your Content:</strong> Any messages, announcements, or files you choose to upload to the app.</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section id="data-usage" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <Shield size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">2. How We Use It</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    We only use your data to make the app work for you. For example:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mt-4 marker:text-brand-primary">
                    <li>Running the platform and making sure your group's tools work properly.</li>
                    <li>Sending you important updates, like if you have a new message or if there's a security alert.</li>
                    <li>Fixing bugs and making the app faster.</li>
                  </ul>
                  
                  <h4 className="text-xl font-bold mt-8 mb-4">Data Retention</h4>
                  <p>
                    We retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. When you delete your account, we will delete your personal data within 30 days from our active servers, and from our secure backups within 90 days.
                  </p>

                  <div className="mt-6 p-4 rounded-xl border font-medium text-center bg-brand-primary/5 border-brand-primary/20 text-brand-primary dark:bg-brand-primary/10">
                    We will never sell your personal data to third parties. Ever.
                  </div>
                </div>
              </section>

              <section id="security" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-500">
                    <Lock size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">3. How We Protect It</h2>
                </div>
                <div className={proseClasses}>
                  <div className="not-prose rounded-xl p-8 my-8 border shadow-sm bg-slate-900 text-slate-300 border-transparent dark:bg-[#111] dark:border-white/10">
                    <h4 className="text-brand-primary font-bold mb-4 flex items-center gap-2">
                      <Shield size={20} /> Our Security Promise
                    </h4>
                    <p className="m-0 text-white leading-relaxed text-lg">
                      We lock down your data like it's ours. Everything is heavily encrypted (both while it's sitting on our servers and while it's traveling across the internet). We also keep your organization's data strictly separated from everyone else's so there are no accidental leaks.
                    </p>
                  </div>
                  <p>
                    We enforce strict technical and administrative access controls designed to ensure our team cannot access your private data unless you explicitly give us temporary permission to help you fix a bug, or if we are legally forced to by a valid court order.
                  </p>
                </div>
              </section>

              <section id="rights" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500">
                    <Shield size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">4. Your Rights</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    It's your data, and you should have complete control over it. You always have the right to:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8 not-prose">
                    <div className="p-6 border rounded-xl transition-all duration-200 border-slate-200 hover:bg-slate-50 bg-white dark:border-white/10 dark:hover:bg-white/5 dark:bg-[#111]">
                      <h5 className="font-bold text-lg mb-2 text-slate-900 dark:text-white">Take It With You</h5>
                      <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">You can download a complete export of your organization's data at any time.</p>
                    </div>
                    <div className="p-6 border rounded-xl transition-all duration-200 border-slate-200 hover:bg-slate-50 bg-white dark:border-white/10 dark:hover:bg-white/5 dark:bg-[#111]">
                      <h5 className="font-bold text-lg mb-2 text-slate-900 dark:text-white">Delete It Forever</h5>
                      <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">If you want out, click a button and we'll wipe your personal data from our servers completely.</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="regional-rights" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                    <Scale size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">5. State & Regional Privacy Rights</h2>
                </div>
                <div className={proseClasses}>
                  <h4 className="text-xl font-bold mt-8 mb-4">California Residents (CCPA/CPRA)</h4>
                  <p>
                    Under the California Consumer Privacy Act, California residents have the right to know what personal information is collected, the right to delete it, and the right to opt-out of the sale or sharing of their information. While <strong>we do not sell your personal data</strong>, you can still exercise your right to know and delete by contacting our privacy team. We will not discriminate against you for exercising your privacy rights.
                  </p>
                  <h4 className="text-xl font-bold mt-8 mb-4">European Economic Area Residents (GDPR)</h4>
                  <p>
                    Our legal basis for processing your personal data is typically to fulfill our contract with you (Terms of Service) and our legitimate interest in providing the Service securely. You have the right to access, rectify, or erase your personal data, restrict its processing, object to its processing, and the right to data portability. You also have the right to lodge a complaint with a supervisory authority.
                  </p>
                </div>
              </section>

              <div className="pt-12">
                <Card className="p-10 rounded-2xl text-center border-none shadow-xl bg-white text-slate-600 dark:bg-[#111] dark:text-slate-300">
                  <h4 className="font-bold mb-4 font-display text-2xl text-slate-900 dark:text-white">Legal & Compliance Contact</h4>
                  <p className="text-base mb-8 max-w-md mx-auto leading-relaxed text-slate-600 dark:text-slate-400">
                    For GDPR requests, DPA agreements, or any security concerns, please contact our Legal Infrastructure team directly.
                  </p>
                  <Button variant="primary" className="rounded-full px-8 py-6 h-auto text-base font-bold shadow-lg shadow-brand-primary/20" onClick={() => window.location.href = '/contact'}>
                    Contact Privacy Team
                  </Button>
                </Card>
              </div>

            </div>
          </div>
        </div>
    </MarketingLayout>
  );
};
